import React from "react";
import { useSelector } from "react-redux";
import "./BaseJourney.css";
import YellowProfileIcon from "../../../assets/timetrails/user_icon_yellow.svg";

const BaseJourney = ({
  currentProgress,
  maxProgress,
  badgeData,
  timelineMarkers,
  progressColor = "#FFA318",
  actualScore = 0, // The actual score to compare with valueThreshold
}) => {
  const { user } = useSelector((state) => state.auth);
  const profileSrc = user?.avatar || YellowProfileIcon;
  const hasUserAvatar = Boolean(user?.avatar);
  const basePathRef = React.useRef(null);
  const [svgPathLength, setSvgPathLength] = React.useState(null);
  const [markerPathLengths, setMarkerPathLengths] = React.useState(null);
  const [animatedProgressLength, setAnimatedProgressLength] = React.useState(0);

  // Generate dynamic path based on exact number of badges
  const getDynamicPath = () => {
    const badgeCount = badgeData.length;

    if (badgeCount <= 3) {
      // Single row: badges 1, 2, 3
      return "M 80 36 L 300 36.1 L 700 36 L 850 36.1";
    } else if (badgeCount === 4) {
      // Two rows: badges 1, 2, 3 on top, badge 4 on bottom right
      return "M 80 60 L 400 60 L 700 60 L 900 60 A 60 60 0 0 1 900 180 L 700 180";
    } else if (badgeCount === 5) {
      // Two rows: badges 1, 2, 3 on top, badges 4, 5 on bottom
      return "M 80 60 L 400 60 L 700 60 L 900 60 A 60 60 0 0 1 900 180 L 700 180 L 580 180";
    } else if (badgeCount === 6) {
      // Two rows: badges 1, 2, 3 on top, badges 4, 5, 6 on bottom
      return "M 80 60 L 400 60 L 700 60 L 900 60 A 60 60 0 0 1 900 180 L 700 180 L 400 180 L 80 180";
    } else if (badgeCount === 7) {
      // Three rows: badges 1, 2, 3 on top, badges 4, 5, 6 on middle, badge 7 on bottom
      return "M 80 60 L 400 60 L 700 60 L 900 60 A 60 60 0 0 1 900 180 L 700 180 L 400 180 L 80 180 A 60 60 0 0 0 80 300 L 270 300";
    } else if (badgeCount === 8) {
      // Three rows: badges 1, 2, 3 on top, badges 4, 5, 6 on middle, badges 7, 8 on bottom
      return "M 80 60 L 400 60 L 700 60 L 900 60 A 60 60 0 0 1 900 180 L 700 180 L 400 180 L 80 180 A 60 60 0 0 0 80 300 L 400 300 L 700 300";
    } else if (badgeCount >= 9) {
      // Three rows: badges 1, 2, 3 on top, badges 4, 5, 6 on middle, badges 7, 8, 9 on bottom
      return "M 80 60 L 400 60 L 700 60 L 900 60 A 60 60 0 0 1 900 180 L 700 180 L 400 180 L 80 180 A 60 60 0 0 0 80 300 L 400 300 L 700 300 L 900 300";
    }

    // Fallback
    return "M 80 36 L 360 36 L 700 36 L 850 36";
  };

  // Calculate total path length based on exact number of badges
  const getTotalPathLength = React.useCallback(() => {
    const badgeCount = badgeData.length;

    if (badgeCount <= 3) {
      return 770; // Single row path
    } else if (badgeCount === 4) {
      return 1020; // Two rows, stops at badge 4
    } else if (badgeCount === 5) {
      return 1280; // Two rows, stops at badge 5
    } else if (badgeCount === 6) {
      return 1540; // Two rows, complete
    } else if (badgeCount === 7) {
      return 1800; // Three rows, stops at badge 7
    } else if (badgeCount === 8) {
      return 2060; // Three rows, stops at badge 8
    } else if (badgeCount >= 9) {
      return 2310; // Three rows, complete
    }

    return 770; // Fallback
  }, [badgeData.length]);

  React.useEffect(() => {
    // Prefer real SVG path length so progress matches badges exactly.
    // Fallback to existing hardcoded lengths if path isn't mounted yet.
    try {
      const node = basePathRef.current;
      if (node && typeof node.getTotalLength === "function") {
        const length = node.getTotalLength();
        if (Number.isFinite(length) && length > 0) {
          setSvgPathLength(length);
          return;
        }
      }
    } catch (e) {
      // ignore and fallback
    }
    setSvgPathLength(null);
  }, [badgeData.length]);

  const getMarkerCoordsForBadgeCount = React.useCallback((badgeCount) => {
    // Coordinates are in the SVG's viewBox coordinate system.
    // These are aligned with the path definitions in getDynamicPath().
    if (badgeCount <= 3) {
      return [
        { x: 260, y: 36 },
        { x: 555, y: 36 },
        { x: 850, y: 36 },
      ];
    }

    if (badgeCount === 4) {
      return [
        { x: 260, y: 60 },
        { x: 555, y: 60 },
        { x: 850, y: 60 },
        { x: 850, y: 180 },
      ];
    }

    if (badgeCount === 5) {
      return [
        { x: 260, y: 60 },
        { x: 555, y: 60 },
        { x: 850, y: 60 },
        { x: 850, y: 180 },
        { x: 555, y: 180 },
      ];
    }

    if (badgeCount === 6) {
      return [
        { x: 260, y: 60 },
        { x: 555, y: 60 },
        { x: 850, y: 60 },
        { x: 850, y: 180 },
        { x: 555, y: 180 },
        { x: 260, y: 180 },
      ];
    }

    if (badgeCount === 7) {
      // Three rows: badges 1-3 (top), 4-6 (middle), 7 (bottom-left-ish)
      return [
        { x: 260, y: 60 },
        { x: 555, y: 60 },
        { x: 850, y: 60 },
        { x: 850, y: 180 },
        { x: 555, y: 180 },
        { x: 260, y: 180 },
        { x: 270, y: 300 },
      ];
    }

    if (badgeCount === 8) {
      return [
        { x: 260, y: 60 },
        { x: 555, y: 60 },
        { x: 850, y: 60 },
        { x: 850, y: 180 },
        { x: 555, y: 180 },
        { x: 260, y: 180 },
        { x: 400, y: 300 },
        { x: 700, y: 300 },
      ];
    }

    if (badgeCount >= 9) {
      return [
        { x: 260, y: 60 },
        { x: 555, y: 60 },
        { x: 850, y: 60 },
        { x: 850, y: 180 },
        { x: 555, y: 180 },
        { x: 260, y: 180 },
        { x: 400, y: 300 },
        { x: 700, y: 300 },
        { x: 900, y: 300 },
      ];
    }

    return null;
  }, []);

  React.useEffect(() => {
    try {
      const node = basePathRef.current;
      if (!node || typeof node.getTotalLength !== "function") {
        setMarkerPathLengths(null);
        return;
      }

      const coords = getMarkerCoordsForBadgeCount(badgeData.length);
      if (!coords || coords.length === 0) {
        setMarkerPathLengths(null);
        return;
      }

      const total = node.getTotalLength();
      const findClosestLengthForPoint = (target) => {
        // coarse scan
        let bestLen = 0;
        let bestDist = Infinity;
        const step = Math.max(2, Math.floor(total / 600)); // adaptive, ~600 samples max
        for (let l = 0; l <= total; l += step) {
          const p = node.getPointAtLength(l);
          const dx = p.x - target.x;
          const dy = p.y - target.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestDist) {
            bestDist = d2;
            bestLen = l;
          }
        }

        // refine around coarse best
        const start = Math.max(0, bestLen - step);
        const end = Math.min(total, bestLen + step);
        for (let l = start; l <= end; l += 1) {
          const p = node.getPointAtLength(l);
          const dx = p.x - target.x;
          const dy = p.y - target.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestDist) {
            bestDist = d2;
            bestLen = l;
          }
        }

        return bestLen;
      };

      const lengths = coords.map(findClosestLengthForPoint);
      setMarkerPathLengths(lengths);
    } catch (e) {
      setMarkerPathLengths(null);
    }
  }, [badgeData.length, getMarkerCoordsForBadgeCount]);

  const getProgressPathLength = React.useCallback(() => {
    // Calculate progress based on actualScore vs the highest valueThreshold
    const thresholds = timelineMarkers.map((marker) => parseInt(marker));

    if (thresholds.length === 0 || actualScore <= 0) {
      return 0; // No progress
    }

    const totalPathLength = svgPathLength ?? getTotalPathLength();

    // If we know where each badge marker sits along the path, interpolate between them.
    if (
      markerPathLengths &&
      markerPathLengths.length >= thresholds.length &&
      thresholds.every((t) => Number.isFinite(t)) &&
      thresholds.every((t, i, arr) => (i === 0 ? true : t >= arr[i - 1]))
    ) {
      const lastIdx = thresholds.length - 1;
      if (actualScore >= thresholds[lastIdx]) {
        return Math.min(
          totalPathLength,
          markerPathLengths[lastIdx] ?? totalPathLength,
        );
      }

      // find segment [i, i+1]
      let i = -1;
      for (let idx = 0; idx < thresholds.length; idx++) {
        if (actualScore >= thresholds[idx]) i = idx;
        else break;
      }

      if (i < 0) {
        // Before first marker: scale up to first marker.
        const firstT = thresholds[0] || 1;
        const firstLen = markerPathLengths[0] ?? 0;
        const pct = Math.max(0, Math.min(actualScore / firstT, 1));
        let len = pct * firstLen;
        if (actualScore > 0 && len < 30) len = 30;
        return Math.min(len, totalPathLength);
      }

      const t0 = thresholds[i];
      const t1 = thresholds[i + 1];
      const l0 = markerPathLengths[i] ?? 0;
      const l1 = markerPathLengths[i + 1] ?? l0;
      const span = t1 - t0;
      const pct = span > 0 ? (actualScore - t0) / span : 0;
      let len = l0 + pct * (l1 - l0);
      if (actualScore > 0 && len < 30) len = 30;
      return Math.min(len, totalPathLength);
    }

    // Fallback: proportional progress across entire path.
    const sortedThresholds = [...thresholds].sort((a, b) => a - b);
    const highestThreshold = sortedThresholds[sortedThresholds.length - 1];
    const progressPercentage =
      highestThreshold > 0 ? Math.min(actualScore / highestThreshold, 1) : 0;
    let pathLength = progressPercentage * totalPathLength;

    // Ensure minimum visible progress when user has some points
    if (actualScore > 0 && pathLength < 30) {
      pathLength = 30; // Minimum 30px progress
    }

    // Ensure progress doesn't exceed total path length
    pathLength = Math.min(pathLength, totalPathLength);

    return pathLength;
  }, [
    actualScore,
    markerPathLengths,
    svgPathLength,
    // `timelineMarkers` is expected to be stable, but include for safety.
    timelineMarkers,
    getTotalPathLength,
  ]);

  const targetProgressLength = React.useMemo(() => {
    return getProgressPathLength();
  }, [getProgressPathLength]);

  React.useEffect(() => {
    // Animate from 0 -> current progress whenever the journey changes (tab switch)
    // or the computed target progress changes.
    setAnimatedProgressLength(0);

    const raf = requestAnimationFrame(() => {
      setAnimatedProgressLength(targetProgressLength);
    });

    return () => cancelAnimationFrame(raf);
  }, [targetProgressLength]);

  // Generate unique ID for this component instance
  const uniqueId = React.useMemo(
    () => Math.random().toString(36).substr(2, 9),
    [],
  );
  const gradientId = `progressGradient-${uniqueId}`;

  return (
    <div
      className={`journey-container ${
        badgeData.length <= 3
          ? "single-row"
          : badgeData.length <= 6
            ? "two-rows"
            : ""
      }`}
    >
      <div className="timeline-section">
        <div
          className={`timeline-container ${
            badgeData.length > 6 ? "three-rows" : ""
          }`}
        >
          {/* Profile Start */}
          <div className="profile-start">
            <div className="profile-picture">
              <img
                src={profileSrc}
                alt={user?.fname ? `${user.fname}'s profile` : "Profile"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: hasUserAvatar ? "cover" : "contain",
                  borderRadius: "100%",
                }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = YellowProfileIcon;
                  e.currentTarget.style.objectFit = "contain";
                }}
              />
            </div>
          </div>

          {/* Timeline Path */}
          <div className="timeline-path">
            {/* Single SVG journey path */}
            <svg
              className="journey-svg"
              viewBox={
                badgeData.length > 6
                  ? "0 0 1000 300"
                  : badgeData.length > 3
                    ? "0 0 1000 200"
                    : "0 0 1000 120"
              }
              preserveAspectRatio="none"
            >
              {/* Base dotted path - dynamic based on exact badge count */}
              <path
                d={getDynamicPath()}
                fill="none"
                stroke="#ffffff"
                strokeWidth={badgeData.length <= 3 ? "0.6" : "1"}
                strokeDasharray="8 3"
                className="journey-path"
                ref={basePathRef}
              />

              {/* Progress path - shows current progress */}
              <path
                d={getDynamicPath()}
                fill="none"
                stroke={`url(#${gradientId})`}
                strokeWidth={badgeData.length <= 3 ? "3.5" : "6"}
                className="progress-path"
                style={{
                  strokeDasharray: `${animatedProgressLength} 999999`,
                  strokeDashoffset: 0,
                }}
              />

              {/* Gradient definition */}
              <defs>
                <linearGradient
                  id={gradientId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    style={{ stopColor: "#FFA318", stopOpacity: 1 }}
                  />
                  <stop
                    offset="49.48%"
                    style={{ stopColor: "#FFC635", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#FFA318", stopOpacity: 1 }}
                  />
                </linearGradient>
              </defs>
            </svg>

            {/* Timeline markers positioned on the journey line */}
            {/* Top row markers */}
            <div
              className={`timeline-marker ${
                actualScore >= parseInt(timelineMarkers[0])
                  ? "active"
                  : "inactive"
              }`}
              style={{
                position: "absolute",
                top: badgeData.length > 6 ? "17.5%" : "26.5%",
                left: "26%",
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "0.9rem",
                background: "#ffffff",
                border:
                  actualScore >= parseInt(timelineMarkers[0])
                    ? "2px solid #FFBE18"
                    : "2px solid #6c757d",
                color:
                  actualScore >= parseInt(timelineMarkers[0])
                    ? "#2c3e50"
                    : "#6c757d",
                zIndex: 10,
              }}
            >
              1
            </div>

            <div
              className={`timeline-marker ${
                actualScore >= parseInt(timelineMarkers[1])
                  ? "active"
                  : "inactive"
              }`}
              style={{
                position: "absolute",
                top: badgeData.length > 6 ? "17.5%" : "26.5%",
                left: "55.5%",
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "0.9rem",
                background: "#ffffff",
                border:
                  actualScore >= parseInt(timelineMarkers[1])
                    ? "2px solid #FFBE18"
                    : "2px solid #6c757d",
                color:
                  actualScore >= parseInt(timelineMarkers[1])
                    ? "#2c3e50"
                    : "#6c757d",
                zIndex: 10,
              }}
            >
              2
            </div>

            <div
              className={`timeline-marker ${
                actualScore >= parseInt(timelineMarkers[2])
                  ? "active"
                  : "inactive"
              }`}
              style={{
                position: "absolute",
                top: badgeData.length > 6 ? "17.5%" : "26.5%",
                left: "85%",
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "0.9rem",
                background: "#ffffff",
                border:
                  actualScore >= parseInt(timelineMarkers[2])
                    ? "2px solid #FFBE18"
                    : "2px solid #6c757d",
                color:
                  actualScore >= parseInt(timelineMarkers[2])
                    ? "#2c3e50"
                    : "#6c757d",
                zIndex: 10,
              }}
            >
              3
            </div>

            {/* Middle row markers - show for badges 4, 5, 6 */}
            {badgeData.length > 3 && (
              <div
                className={`timeline-marker ${
                  actualScore >= parseInt(timelineMarkers[3])
                    ? "active"
                    : "inactive"
                }`}
                style={{
                  position: "absolute",
                  top: badgeData.length > 6 ? "57.5%" : "86.5%",
                  left: "85%",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  background: "#ffffff",
                  border:
                    actualScore >= parseInt(timelineMarkers[3])
                      ? "2px solid #FFBE18"
                      : "2px solid #6c757d",
                  color:
                    actualScore >= parseInt(timelineMarkers[3])
                      ? "#2c3e50"
                      : "#6c757d",
                  zIndex: 10,
                }}
              >
                4
              </div>
            )}

            {badgeData.length > 4 && (
              <div
                className={`timeline-marker ${
                  actualScore >= parseInt(timelineMarkers[4])
                    ? "active"
                    : "inactive"
                }`}
                style={{
                  position: "absolute",
                  top: badgeData.length > 6 ? "57.5%" : "86.5%",
                  left: "55.5%",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  background: "#ffffff",
                  border:
                    actualScore >= parseInt(timelineMarkers[4])
                      ? "2px solid #FFBE18"
                      : "2px solid #6c757d",
                  color:
                    actualScore >= parseInt(timelineMarkers[4])
                      ? "#2c3e50"
                      : "#6c757d",
                  zIndex: 10,
                }}
              >
                5
              </div>
            )}

            {badgeData.length > 5 && (
              <div
                className={`timeline-marker ${
                  actualScore >= parseInt(timelineMarkers[5])
                    ? "active"
                    : "inactive"
                }`}
                style={{
                  position: "absolute",
                  top: badgeData.length > 6 ? "57.5%" : "86.5%",
                  left: "26%",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  background: "#ffffff",
                  border:
                    actualScore >= parseInt(timelineMarkers[5])
                      ? "2px solid #FFBE18"
                      : "2px solid #6c757d",
                  color:
                    actualScore >= parseInt(timelineMarkers[5])
                      ? "#2c3e50"
                      : "#6c757d",
                  zIndex: 10,
                }}
              >
                6
              </div>
            )}

            {/* Third row markers - only show if more than 6 badges */}
            {badgeData.length > 6 && (
              <div
                className={`timeline-marker ${
                  actualScore >= parseInt(timelineMarkers[6])
                    ? "active"
                    : "inactive"
                }`}
                style={{
                  position: "absolute",
                  top: "701px",
                  left: "26%",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  background: "#ffffff",
                  border:
                    actualScore >= parseInt(timelineMarkers[6])
                      ? "2px solid #FFBE18"
                      : "2px solid #6c757d",
                  color:
                    actualScore >= parseInt(timelineMarkers[6])
                      ? "#2c3e50"
                      : "#6c757d",
                  zIndex: 10,
                }}
              >
                7
              </div>
            )}

            {badgeData.length > 7 && (
              <div
                className={`timeline-marker ${
                  actualScore >= parseInt(timelineMarkers[7])
                    ? "active"
                    : "inactive"
                }`}
                style={{
                  position: "absolute",
                  top: "701px",
                  left: "55.5%",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  background: "#ffffff",
                  border:
                    actualScore >= parseInt(timelineMarkers[7])
                      ? "2px solid #FFBE18"
                      : "2px solid #6c757d",
                  color:
                    actualScore >= parseInt(timelineMarkers[7])
                      ? "#2c3e50"
                      : "#6c757d",
                  zIndex: 10,
                }}
              >
                8
              </div>
            )}

            {badgeData.length > 8 && (
              <div
                className={`timeline-marker ${
                  actualScore >= parseInt(timelineMarkers[8])
                    ? "active"
                    : "inactive"
                }`}
                style={{
                  position: "absolute",
                  top: "701px",
                  left: "85%",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  background: "#ffffff",
                  border:
                    actualScore >= parseInt(timelineMarkers[8])
                      ? "2px solid #FFBE18"
                      : "2px solid #6c757d",
                  color:
                    actualScore >= parseInt(timelineMarkers[8])
                      ? "#2c3e50"
                      : "#6c757d",
                  zIndex: 10,
                }}
              >
                9
              </div>
            )}
          </div>

          {/* Combined Badges and Markers */}
          <div className="badges-and-markers">
            {/* Top row badges with markers below */}
            <div
              className="badge-marker-group"
              style={{
                top: badgeData.length > 6 ? "-170px" : "-110px",
                left: "calc(26% - 60px)",
              }}
            >
              <div className="badge">
                <img
                  src={badgeData[0]?.image || ""}
                  alt={badgeData[0]?.title || "Badge 1"}
                  className={`badge-image ${
                    badgeData[0]?.isAchieved ? "achieved" : "locked"
                  }`}
                />
              </div>
            </div>

            <div
              className="badge-marker-group"
              style={{
                top: badgeData.length > 6 ? "-170px" : "-110px",
                left: "calc(55.5% - 60px)",
              }}
            >
              <div className="badge">
                <img
                  src={badgeData[1]?.image || ""}
                  alt={badgeData[1]?.title || "Badge 2"}
                  className={`badge-image ${
                    badgeData[1]?.isAchieved ? "achieved" : "locked"
                  }`}
                />
              </div>
            </div>

            <div
              className="badge-marker-group"
              style={{
                top: badgeData.length > 6 ? "-170px" : "-110px",
                left: "calc(85% - 60px)",
              }}
            >
              <div className="badge">
                <img
                  src={badgeData[2]?.image || ""}
                  alt={badgeData[2]?.title || "Badge 3"}
                  className={`badge-image ${
                    badgeData[2]?.isAchieved ? "achieved" : "locked"
                  }`}
                />
              </div>
            </div>

            {/* Bottom row badges with markers above - distribute remaining badges */}
            {badgeData.length > 3 && (
              <div
                className="badge-marker-group"
                style={{
                  top: badgeData.length > 6 ? "120px" : "205px",
                  left: "calc(84.5% - 60px)",
                }}
              >
                <div className="badge">
                  <img
                    src={badgeData[3]?.image || ""}
                    alt={badgeData[3]?.title || "Badge 4"}
                    className={`badge-image ${
                      badgeData[3]?.isAchieved ? "achieved" : "locked"
                    }`}
                  />
                </div>
              </div>
            )}

            {badgeData.length > 4 && (
              <div
                className="badge-marker-group"
                style={{
                  top: badgeData.length > 6 ? "120px" : "205px",
                  left: "calc(55.5% - 60px)",
                }}
              >
                <div className="badge">
                  <img
                    src={badgeData[4]?.image || ""}
                    alt={badgeData[4]?.title || "Badge 5"}
                    className={`badge-image ${
                      badgeData[4]?.isAchieved ? "achieved" : "locked"
                    }`}
                  />
                </div>
              </div>
            )}

            {badgeData.length > 5 && (
              <div
                className="badge-marker-group"
                style={{
                  top: badgeData.length > 6 ? "120px" : "205px",
                  left: "calc(26% - 60px)",
                }}
              >
                <div className="badge">
                  <img
                    src={badgeData[5]?.image || ""}
                    alt={badgeData[5]?.title || "Badge 6"}
                    className={`badge-image ${
                      badgeData[5]?.isAchieved ? "achieved" : "locked"
                    }`}
                  />
                </div>
              </div>
            )}

            {/* Third row badges - only show if more than 6 badges */}
            {badgeData.length > 6 && (
              <div
                className="badge-marker-group"
                style={{ top: "410px", left: "calc(26% - 60px)" }}
              >
                <div className="badge">
                  <img
                    src={badgeData[6]?.image || ""}
                    alt={badgeData[6]?.title || "Badge 7"}
                    className={`badge-image ${
                      badgeData[6]?.isAchieved ? "achieved" : "locked"
                    }`}
                  />
                </div>
              </div>
            )}

            {badgeData.length > 7 && (
              <div
                className="badge-marker-group"
                style={{ top: "410px", left: "calc(26% - 60px)" }}
              >
                <div className="badge">
                  <img
                    src={badgeData[7]?.image || ""}
                    alt={badgeData[7]?.title || "Badge 8"}
                    className={`badge-image ${
                      badgeData[7]?.isAchieved ? "achieved" : "locked"
                    }`}
                  />
                </div>
              </div>
            )}

            {badgeData.length > 8 && (
              <div
                className="badge-marker-group"
                style={{ top: "410px", left: "calc(26% - 60px)" }}
              >
                <div className="badge">
                  <img
                    src={badgeData[8]?.image || ""}
                    alt={badgeData[8]?.title || "Badge 9"}
                    className={`badge-image ${
                      badgeData[8]?.isAchieved ? "achieved" : "locked"
                    }`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseJourney;
